'use client';

/**
 * Toast Component
 *
 * Self-managing toast notification with an imperative API.
 * Positioned above the pay button on mobile, fixed to viewport bottom on desktop.
 *
 * Usage:
 *   import Toast, { showToast } from './components/Toast';
 *
 *   // Render once (no props needed):
 *   <Toast />
 *
 *   // Trigger from anywhere:
 *   showToast('Default payment updated');
 *
 * If called while the component is unmounted (e.g., during a screen transition),
 * the message is queued and displayed when the component mounts.
 */

import React, {
  useState, useEffect, useCallback, useRef
} from 'react';
import cn from 'classnames';
import classes from './classes.module.scss';

const DEFAULT_DURATION = 3000;

let listener: ((message: React.ReactNode) => void) | null = null;
let pendingMessage: React.ReactNode | null = null;
let pendingTimer: ReturnType<typeof setTimeout> | null = null;

export const showToast = (message: React.ReactNode): void => {
  if (listener) {
    listener(message);
  } else {
    pendingMessage = message;
    if (pendingTimer) clearTimeout(pendingTimer);
    pendingTimer = setTimeout(() => { pendingMessage = null; }, DEFAULT_DURATION);
  }
};

const Toast: React.FC = () => {
  const [visible, setVisible] = useState(false);
  const [message, setMessage] = useState<React.ReactNode>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const show = useCallback((msg: React.ReactNode) => {
    setMessage(msg);
    setVisible(true);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setVisible(false), DEFAULT_DURATION);
  }, []);

  useEffect(() => {
    listener = show;

    if (pendingMessage) {
      show(pendingMessage);
      pendingMessage = null;
      if (pendingTimer) { clearTimeout(pendingTimer); pendingTimer = null; }
    }

    return () => {
      listener = null;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [show]);

  return (
    <div className={ cn(classes.toastWrapper, { [classes.toastWrapperVisible]: visible }) } data-testid="purchase-flow-toast">
      <div className={ classes.toast }>
        {message}
      </div>
    </div>
  );
};

export default Toast;
